import { chapterStoreKey, loadProductChapters, loadProductSeniority, seniorityKey } from '../productMock';
import { productStore } from './productStore';
import {
  copyVersionValues,
  openYear,
  readVersions,
  splitAt,
  startOfYear,
  writeVersions,
  yearOf,
} from './productVersions';

/** What giving somebody a new level or chapter did to one product. */
export type LevelChange = 'created' | 'updated' | 'skipped';

/** A person's profile values a product can keep its own copy of; an absent one is left alone. */
export type ProfileValues = { level?: string; chapter?: string };

const write = (
  productId: string,
  version: string | undefined,
  membershipId: string,
  values: ProfileValues,
  onlyMissing: boolean,
) => {
  const stores = [
    { value: values.level, key: seniorityKey, load: loadProductSeniority },
    { value: values.chapter, key: chapterStoreKey, load: loadProductChapters },
  ];
  stores.forEach(({ value, key, load }) => {
    if (value === undefined) return;
    const held = load(productId, version);
    if (onlyMissing && membershipId in held) return;
    productStore.setItem(key(productId, version), JSON.stringify({ ...held, [membershipId]: value }));
  });
};

/** Copies the profile's values into every version that has none for them, so a profile change cannot reprice it. */
export const keepProfileValues = (productId: string, membershipId: string, values: ProfileValues): void => {
  const versions = readVersions(productId);
  (versions.length ? versions.map(version => version.number) : [undefined]).forEach(version =>
    write(productId, version, membershipId, values, true),
  );
};

/** The year a version would split into must exist first, as the product page opens it on a first visit. */
const withYearOpen = (productId: string, today: string) => {
  const opened = openYear(readVersions(productId), today);
  if (!opened.created) return opened.versions;
  if (opened.from) opened.opened.forEach(version => copyVersionValues(productId, opened.from!.number, version.number));
  writeVersions(productId, opened.versions);
  return opened.versions;
};

/** New values from `startDate` on; skipped where a later version exists, since splitting an earlier one renumbers it. */
export const setProfileFrom = (
  productId: string,
  membershipId: string,
  values: ProfileValues,
  startDate: string,
  today: string,
): LevelChange => {
  const versions = withYearOpen(productId, today);
  // A sprint begun last year cannot split this year's versions, so the level applies from 1 January.
  const from = startDate < startOfYear(yearOf(today)) ? startOfYear(yearOf(today)) : startDate;
  const last = versions[versions.length - 1];
  if (!last) return 'skipped';

  let target = last.number;
  if (last.startDate !== from) {
    const split = splitAt(versions, from);
    if (!split.created) return 'skipped';
    copyVersionValues(productId, last.number, split.created.number);
    writeVersions(productId, split.versions);
    target = split.created.number;
  }

  write(productId, target, membershipId, values, false);
  return last.startDate === from ? 'updated' : 'created';
};
